import { THEME_PROPERTY_REGISTRY, type ThemeProperty } from './themePropertyRegistry';

const VARIABLES_PREFIX = 'variables.';

// The seller API reads and writes every theme property except these, as registry paths without `variables.`.
// Order bump styling: order bumps are a labs-gated beta and stay out of the public API and SDK until they ship.
// `locale`: the page's top-level `locale` field is its one writer.
export const THEME_OVERRIDES_API_EXCLUDED_PATHS = ['locale'] as const;
export const THEME_OVERRIDES_API_EXCLUDED_PREFIXES = [
  'elements.checkout.form.orderBumps.',
] as const;

const isExcluded = (path: string) =>
  (THEME_OVERRIDES_API_EXCLUDED_PATHS as readonly string[]).includes(path) ||
  THEME_OVERRIDES_API_EXCLUDED_PREFIXES.some(prefix => path.startsWith(prefix));

let exposedPaths: Set<string> | undefined;

export function getExposedThemeProperties(): ThemeProperty[] {
  return THEME_PROPERTY_REGISTRY.filter(
    property =>
      property.path.startsWith(VARIABLES_PREFIX) &&
      !isExcluded(property.path.slice(VARIABLES_PREFIX.length)),
  );
}

/** Whether `path` is a theme property the API exposes. `path` is relative to `variables.`, e.g. `tokens.colorPrimary`. */
export function isThemePathExposedInApi(path: string): boolean {
  exposedPaths ??= new Set(
    getExposedThemeProperties().map(property => property.path.slice(VARIABLES_PREFIX.length)),
  );
  return exposedPaths.has(path);
}

export const CUSTOM_CSS_MAX_LENGTH = 50_000;

const BADGE_NOTE = 'Changing it needs a paid subscription, not a free plan or a trial.';

// What an API caller needs beyond the registry description: a value format that differs from the type's, and the
// plan checks the API applies.
const API_NOTES: Record<string, string> = {
  customCss: `Takes CSS, up to ${CUSTOM_CSS_MAX_LENGTH.toLocaleString('en-US')} characters, without \`</style\`. Setting it needs a plan that includes custom CSS.`,
  'elements.poweredByCheckoutPage.enabled': BADGE_NOTE,
  'elements.poweredByStripe.enabled': BADGE_NOTE,
  'elements.poweredByStripe.styles.badgeStyle': BADGE_NOTE,
};

/** A theme property's description for API callers: the registry description plus any API note. */
export function describeThemePropertyForApi(property: ThemeProperty): string {
  const description = property.description.trim();
  const note = API_NOTES[property.path.slice(VARIABLES_PREFIX.length)];
  if (!note) return description;

  return `${/[.!?]$/.test(description) ? description : `${description}.`} ${note}`;
}
